import { useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectAllFilters } from '../../../../extractFromState';
import { initialState } from '../../../../reducers/allFilters';
import isEqual from 'lodash/isEqual';

const FIELD_KEYS = ['ticketStatus', 'sortOrder', 'dateRange', 'businessIds', 'selectedBrandOptions'];

export const useFilterTouchedState = ({ localIdentifierType, localIdentifierValue }) => {
  const filters = useSelector(selectAllFilters);
  const lastSearchedRef = useRef(null); // null = no search performed yet

  // NOTE(vlad|SPD-14367|2026-03-05): On mount with URL params, filters differ from defaults but no search happened
  // → treat defaults as the "last searched" baseline so differences show as touched
  const hasUrlFilters = useMemo(() => {
    if (lastSearchedRef.current !== null) {
      return false;
    }

    return (
      FIELD_KEYS.some((key) => !isEqual(filters[key], initialState[key])) ||
      (filters.identifier?.value && filters.identifier.value !== initialState.identifier.value)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // only check on mount

  const baseline = lastSearchedRef.current ?? (hasUrlFilters ? initialState : filters);

  const touchedFields = {
    ticketStatus: !isEqual(filters.ticketStatus, baseline.ticketStatus),
    sortOrder: !isEqual(filters.sortOrder, baseline.sortOrder),
    dateRange: !isEqual(filters.dateRange, baseline.dateRange),
    businessIds: !isEqual(filters.businessIds, baseline.businessIds),
    selectedBrandOptions: !isEqual(filters.selectedBrandOptions, baseline.selectedBrandOptions),
    identifierType: localIdentifierType !== (baseline.identifier?.type || ''),
    identifierValue: localIdentifierValue !== (baseline.identifier?.value || ''),
  };

  const isSearchPending = Object.values(touchedFields).some(Boolean);

  const markSearched = () => {
    lastSearchedRef.current = {
      ...filters,
      identifier: { type: localIdentifierType, value: localIdentifierValue },
    };
  };

  return { touchedFields, isSearchPending, markSearched };
};
